import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listEntityDrive, mapApiEntityDriveToUi } from '../../../services/entityDrive.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_META = Object.freeze({ totalItems: 0, totalPages: 0, currentPage: 1, perPage: 0, hasNext: false, hasPrevious: false, nextCursor: null });

/**
 * One folder of a record's drive (CT-5717): its rows, the tree and breadcrumb
 * around it, and its claim extraction. Race guard, serialized keys, memoized
 * return; the server narrows. `setExtractionEnabled` lets the switch update
 * optimistically (and roll back) without a refetch.
 */
export function useEntityDrive({ entityType, entityId, folderId, page = 1, pageSize = 10, orderDirection, quickFilter = 'all', filters, search } = {}) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);
  // Unmounted (another record took its place): a refresh kept by a late answer asks nothing.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId || !mountedRef.current) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
      }
      try {
        const parsedSearch = JSON.parse(searchKey);
        const { body, meta: rawMeta } = await listEntityDrive({
          entityType,
          entityId,
          folderId,
          page,
          pageSize,
          orderDirection,
          quickFilter,
          filters: JSON.parse(filtersKey),
          ...(parsedSearch ? { search: parsedSearch } : {}),
        });
        if (requestId !== fetchIdRef.current) return;
        setData(mapApiEntityDriveToUi(body ?? {}));
        setMeta(normalizePagedMeta(rawMeta) ?? EMPTY_META);
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setData(null);
        setMeta(EMPTY_META);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? 'Failed to load the drive');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId, folderId, page, pageSize, orderDirection, quickFilter, filtersKey, searchKey],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);
  const setExtractionEnabled = useCallback(
    (enabled) => setData((current) => (current?.extraction ? { ...current, extraction: { ...current.extraction, enabled, inherited: false } } : current)),
    [],
  );

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ data, meta, loading, error, accessDenied: status === 403, refetch, refresh, setExtractionEnabled }),
    [data, meta, loading, error, status, refetch, refresh, setExtractionEnabled],
  );
}

export default useEntityDrive;
